import { useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { Loader2, Check, ChevronUp } from "lucide-react"
import { cn } from "@/lib/utils"
import { useScrollSequence, ThinkingDots } from "./shared"

type RefinementStep = "idle" | "userMessage" | "thinking" | "aiResponse" | "rendering" | "done"

const USER_MSG = "Make the second clip more dynamic — add a slow camera push and boost the neon colors"
const AI_MSG =
    "I've updated clip 2 with a slow dolly push-in and increased the neon saturation. The puddle reflections should be more vivid now. Regenerating the clip."

const STEPS: RefinementStep[] = ["userMessage", "thinking", "rendering", "done", "aiResponse"]
const SEQUENCE = [
    { delay: 400 },
    { delay: 600 },
    { delay: 1000 },
    { delay: 1200 },
    { delay: 400 },
]

const ORDER: RefinementStep[] = ["idle", "userMessage", "thinking", "rendering", "done", "aiResponse"]

export function AgentWorkflowBlock() {
    const [step, setStep] = useState<RefinementStep>("idle")

    const { ref } = useScrollSequence(
        SEQUENCE,
        (i) => setStep(STEPS[i]),
        () => setStep("idle"),
        { amount: 0.6, margin: "20px 0px -10px 0px" },
    )

    const si = ORDER.indexOf(step)

    return (
        <div
            ref={ref}
            className="relative flex items-center justify-center px-6 md:px-12 pt-6 md:pt-12 pb-2 overflow-visible"
        >
            <div className="w-full max-w-lg bg-white rounded-xl border border-neutral-200 overflow-hidden shadow-sm flex flex-col">
                <div className="px-4 pt-4 pb-4 space-y-4 min-h-[260px]">
                    <AnimatePresence>
                        {si >= 1 && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="flex flex-col gap-1 items-end"
                            >
                                <div className="max-w-[85%] inline-block rounded-2xl px-4 py-2.5 bg-neutral-700 text-white">
                                    <p className="text-sm leading-relaxed">{USER_MSG}</p>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {step === "thinking" && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.25 }}
                                className="flex flex-col gap-1 items-start"
                            >
                                <div className="max-w-[85%] inline-block rounded-2xl px-4 py-2.5 bg-neutral-100">
                                    <ThinkingDots />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Rendering pill — shows during rendering only */}
                    <AnimatePresence>
                        {step === "rendering" && (
                            <motion.div
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -4 }}
                                transition={{ duration: 0.25 }}
                                className="flex justify-center py-1"
                            >
                                <div className={cn(
                                    "text-[11px] font-medium px-3 py-1 rounded-full",
                                    "border border-blue-200 flex items-center gap-1.5 overflow-hidden relative"
                                )}>
                                    <span className="absolute inset-0 bg-blue-100 rounded-full animate-pulse" />
                                    <Loader2 size={10} className="animate-spin text-blue-600 relative z-10" />
                                    <span className="text-blue-700 relative z-10">Rendering clip 2</span>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Done pill — stays visible once it appears */}
                    <AnimatePresence>
                        {si >= 4 && (
                            <motion.div
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.25 }}
                                className="flex justify-center py-1"
                            >
                                <div className={cn(
                                    "text-[11px] font-medium px-3 py-1 rounded-full",
                                    "border border-emerald-200 flex items-center gap-1.5"
                                )}>
                                    <Check size={10} className="text-emerald-600" />
                                    <span className="text-emerald-700">Clip 2 updated</span>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {si >= 5 && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.35 }}
                                className="flex flex-col gap-1 items-start"
                            >
                                <div className="max-w-[85%] inline-block rounded-2xl px-4 py-2.5 bg-neutral-100 text-neutral-700">
                                    <p className="text-sm leading-relaxed">{AI_MSG}</p>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div className="px-4 pb-4 pt-2">
                    <div className="flex gap-2 items-end">
                        <div className="flex-1 rounded-lg border border-neutral-300 px-3 py-2.5">
                            <span className="text-sm text-neutral-400">Describe changes...</span>
                        </div>
                        <button className="shrink-0 w-10 h-10 rounded-lg bg-neutral-200 text-neutral-400 flex items-center justify-center">
                            <ChevronUp size={18} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
